<template>
  <div class="flex flex-col gap-2 w-full">
    <div v-if="label" class="flex items-center justify-between">
      <label for="slider-with-input" class="font-semibold">{{ label }}</label>
      <a-input
        v-if="unit"
        id="slider-with-input"
        :value="formattedValue"
        readonly
        class="w-20 text-center"
      />
      <a-input-number
        v-else
        id="slider-with-input"
        :value="value"
        :min="min"
        :max="max"
        :step="effectiveStep"
        :precision="precision"
        class="w-14"
        @change="handleChange"
      />
    </div>
    <div class="flex flex-col dark">
      <a-slider
        :value="value"
        :min="min"
        :max="max"
        :step="effectiveStep"
        :tip-formatter="tipFormatter"
        @change="handleChange"
      />
      <div
        v-if="minLabel || maxLabel"
        class="flex items-center justify-between"
      >
        <span class="text-sm secondary_color">{{ minLabel }}</span>
        <span class="text-sm secondary_color">{{ maxLabel }}</span>
      </div>
    </div>
  </div>
</template>
  
  <script setup>
import { computed } from "vue";
import { calculatePrecision } from "@/src/utils/numberUtils.js";
import { formatTimeDisplay } from "@/src/utils/dateTimeUtils.js";

const props = defineProps({
  value: {
    type: Number,
    default: 0,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  label: {
    type: String,
    default: "",
  },
  minLabel: {
    type: String,
    default: "",
  },
  maxLabel: {
    type: String,
    default: "",
  },
  unit: {
    type: String,
    default: "",
  },
  precision: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["update:value", "change"]);

const precision = computed(() => 
  props.precision !== null ? props.precision : calculatePrecision(props.step)
);

const effectiveStep = computed(() => {
  if (props.precision !== null && props.precision > 0) {
    return 1 / Math.pow(10, props.precision);
  }
  return props.step;
});

const formattedValue = computed(() => formatValue(props.value));

const tipFormatter = (value) => formatValue(value);

const formatValue = (value) => {
  try {
    if (!props.unit) {
      return value;
    }
    return formatTimeDisplay(value, props.unit);
  } catch (error) {
    console.error("Error formatting value:", error);
    return value;
  }
};

const handleChange = (newValue) => {
  emit("update:value", newValue);
  emit("change", newValue);
};
</script>  

<style scoped>
:deep(.ant-slider-track) {
  background-color: var(--text-color) !important;
}
:deep(.ant-slider-handle:after) {
  box-shadow: 0 0 0 4px var(--text-color) !important;
}
</style>
